/**
 * A key from the Profile's own event types (#197). The list is the
 * Company's, served on the profile config, so the form holds no list of
 * its own; the one key it knows is Other, which is on every list and
 * opens the "Please specify" field.
 */
export type EventType = string;

export const OTHER_EVENT_TYPE = "other";

/** One answer to "What kind of event is it?", as a tile renders it. */
export interface EventTypeOption {
  value: EventType;
  label: string;
}

export const otherEventTypeOption: EventTypeOption = {
  value: OTHER_EVENT_TYPE,
  label: "Other",
};

export const MAX_TECH_RIDER_BYTES = 10 * 1024 * 1024;

export type AttendanceBand = "under_50" | "50_200" | "over_200" | "unsure";

/** Attendance escape hatch: a band answer is enough to continue. The
 *  representative number keeps the backend package derivation intact
 *  (small <50 / medium / large >200 tiers); `unsure` sends no attendance
 *  at all — the backend treats it as optional. An exact number, when
 *  given, always wins over the band. */
export const attendanceBands: {
  value: AttendanceBand;
  label: string;
  representative?: number;
}[] = [
  { value: "under_50", label: "Under 50", representative: 40 },
  { value: "50_200", label: "50–200", representative: 120 },
  { value: "over_200", label: "200+", representative: 250 },
  { value: "unsure", label: "Not sure yet" },
];

/** Answers to a profile's `contentStep.packQuestions`, keyed by question id.
 *  Checkbox answers are booleans; text/textarea/select answers are strings. */
export type InquiryProfileAnswers = Record<string, string | boolean>;

export interface PackageFormData {
  eventType?: EventType;
  eventTypeOther?: string;
  eventName?: string;
  organization?: string;
  eventDate?: string;
  eventEndDate?: string;
  eventStartTime?: string;
  eventEndTime?: string;
  attendance?: number;
  attendanceBand?: AttendanceBand;

  /** The one flat content survivor: the document parse prefills it from the
   *  rider's artist name (no step renders it). Every other content answer
   *  lives in `profileAnswers` — the flat live-sound content fields were
   *  deleted with the flat content transport (#240 candidate A1). */
  bandNames?: string;
  additionalInfo?: string;
  /** Answers to the profile's declared `contentStep.packQuestions`, keyed by
   *  question id — the only content transport for every profile. */
  profileAnswers?: InquiryProfileAnswers;

  location?: string;
  /** Coordinates captured when the customer picked an autocomplete
   *  suggestion (geocoded on pick). Cleared whenever `location` is edited
   *  afterwards — they are only valid for `siteGeocodedLocation`, the exact
   *  string that was picked. */
  siteLat?: number;
  siteLng?: number;
  siteGeocodedLocation?: string;
  venueContact?: string;
  /** The customer asking for someone to come out and look at the site
   *  before the quote, and the day and rough time that would suit. A core
   *  field every discipline asks — any rental company can send someone out
   *  — so it rides the shared venue step, not a Profile's pack. */
  siteVisit?: boolean;
  siteVisitWhen?: string;
  indoorOutdoor?: string;
  wetWeatherPlan?: string;
  powerAccess?: string;

  hasStage?: boolean;
  stageDetails?: string;

  siteAvailableFrom?: string;
  callTime?: string;
  packOutTime?: string;

  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  /** How the Customer wants to be got back to, and — for a call or a
   *  text — when suits them. A core field every Profile asks on its
   *  contact step: any rental company rings a Customer back, and The Rock
   *  Factory's own quote form asked both where ours asked neither. */
  preferredContactMethod?: ContactMethod;
  contactWhen?: string;
  details?: string;
}

/** How a Customer asked to be got back to (CONTEXT.md's Contact Preference). */
export type ContactMethod = "email" | "call" | "text";

/** The three ways, in the order the contact step offers them. */
export const contactMethods: readonly { value: ContactMethod; label: string }[] = [
  { value: "email", label: "Email" },
  { value: "call", label: "Phone call" },
  { value: "text", label: "Text" },
];

export type Step =
  | "tech-rider"
  | "event-details"
  | "contact"
  | "venue"
  | "content";

export type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; statusToken?: string }
  | { status: "error"; message: string };
